import React from 'react'
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Cell,
  ReferenceLine,
} from 'recharts'
import { formatDate } from '../../utils/chartUtils'
import { AnalysisHistoricalResults, ScoreData } from '../../types'
import usePadding from '../../hooks/useChartBarsPositions'
import RenderCustomLegend from '../RenderCustomLegend'
import { useTranslation } from 'react-i18next'

interface EvolutionCognitiveBarChartProps {
  data: AnalysisHistoricalResults | null
}

const EvolutionCognitiveBarChart: React.FC<EvolutionCognitiveBarChartProps> = ({
  data,
}) => {
  const { t } = useTranslation()
  const reference = [
    {
      label: '',
      lower: 0,
      upper: 2,
      color: '#226F54',
    },
    {
      label: t(
        'myEvolution.cognitive.chart.pfeifferChart.yAxisLabels.noAppearance',
      ),
      lower: 2,
      upper: 2,
      color: '#226F54',
    },
    {
      label: t('myEvolution.cognitive.chart.pfeifferChart.yAxisLabels.low'),
      lower: 3,
      upper: 4,
      color: '#F7E06D',
    },
    {
      label: t(
        'myEvolution.cognitive.chart.pfeifferChart.yAxisLabels.moderate',
      ),
      lower: 5,
      upper: 7,
      color: '#AD941A',
    },
    {
      label: t('myEvolution.cognitive.chart.pfeifferChart.yAxisLabels.severe'),
      lower: 8,
      upper: 10,
      color: '#B3261E',
    },
  ]

  const yAxisLabels = reference.map((ref) => ref.label)

  const padding = usePadding()

  const getBarColor = (score: number) => {
    if (score >= 0 && score <= 2) {
      return '#226F54' // Color para puntajes entre 0 y 2
    } else if (score >= 3 && score <= 4) {
      return '#F7E06D' // Color para puntajes entre 3 y 4
    } else if (score >= 5 && score <= 7) {
      return '#F7E06D' // Color para puntajes entre 5 y 7
    } else if (score >= 8 && score <= 10) {
      return '#B3261E' // Color para puntajes entre 8 y 10
    }
    return '#226F54'
  }

  const minScore = 0
  const maxScore = 10
  const numberOfLines = 4.1
  const interval = (maxScore - minScore) / numberOfLines

  const sortedData = data?.data?.attributes?.scores
    ?.find((s: ScoreData) => s.name === 'PFEIFFER')
    ?.data.sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    )

  const validData = sortedData ? sortedData.slice(-3) : []

  return (
    <div className="w-full sm:w-[60%]">
      <ResponsiveContainer height={450} style={{ marginTop: '-12px' }}>
        <BarChart
          data={validData}
          margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
        >
          <XAxis
            dataKey="date"
            tickFormatter={formatDate}
            scale="point"
            padding={padding}
            axisLine={true}
            tickLine={true}
          />

          <YAxis
            domain={[minScore, maxScore]}
            ticks={reference.map((_, index) => minScore + index * interval)}
            tickFormatter={(value, index) =>
              yAxisLabels[index] !== '' ? yAxisLabels[index] : ''
            }
            interval={0}
            tickCount={numberOfLines}
            width={120}
            axisLine={true}
            tickLine={true}
            tick={{ fontSize: 16 }}
          />

          {reference.map((ref, index) => (
            <ReferenceLine
              key={`ref-line-${index}`}
              y={minScore + index * interval}
              stroke={ref.color}
              strokeDasharray="4 4"
            />
          ))}

          <Tooltip
            formatter={(value) =>
              value === 'not available' ? t('myEvolution.valueNotAvailable') : `${value} pts`
            }
            labelFormatter={formatDate}
          />
          <Legend
            content={
              <RenderCustomLegend
                lowLabel={
                  t('myEvolution.cognitive.chart.pfeifferChart.legend.low') ||
                  ''
                }
                mediumLabel={
                  t(
                    'myEvolution.cognitive.chart.pfeifferChart.legend.medium',
                  ) || ''
                }
                highLabel={
                  t('myEvolution.cognitive.chart.pfeifferChart.legend.high') ||
                  ''
                }
              />
            }
          />

          <Bar
            dataKey="score"
            barSize={40}
            fillOpacity={1}
            stroke="none"
            name="Puntuación"
            minPointSize={2}
          >
            {data?.data?.attributes?.scores
              ?.find((s: ScoreData) => s.name === 'PFEIFFER')
              ?.data.map(
                (entry, index) => (
                  console.log(entry.score),
                  (
                    <Cell
                      key={`cell-${index}`}
                      fill={getBarColor(entry.score)}
                      style={{ visibility: entry.score.toString() === 'not available' ? 'hidden' : 'visible' }}
                    />
                  )
                ),
              )}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

export default EvolutionCognitiveBarChart
